import { error } from 'node:console';
import * as React from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/index';
import { Afr, AuditMessage, generateLocalStateFromEventProperties, getRow, getSelectedAfr, getTable, isAFRAuditRequired } from '../../redux/AfrSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { selectUser, selectUserLogged } from '../../redux/UserSlice';
import { logerUser } from '../../redux/UsersSlice';
import {
    addBusinessRulesErrors, AFRError, DeletionCriteria, ErrorSummaryAFRSection,
    getErrorList, getTotalErrorCountBySection, Section, setDisplayErrors, updateErrors,
    ValidationContext, getValidationsForAfr, validateAfr, ErrorGrid, getSupplementalGridErrorList
} from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { getSelectedEvents } from '../../redux/EventsSlice';
import { getTableFacts, getReplaceTableFacts, getDimensionSectionMap } from '../../redux/MiscSlice';

const NavigatorIconNotification = (props:
    { page: string, section: string }
) => {

    const dispatch = useAppDispatch();
    const errorList = useAppSelector(getErrorList);
    var currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const selectedEntity: Entity | null = useAppSelector(getSelectedEntity);
    const auditMessage: AuditMessage = useAppSelector(isAFRAuditRequired);
    const currentEvents = useAppSelector(getSelectedEvents);
    const pendingTableFacts = useAppSelector(getTableFacts);
    const pendingReplaceTableFacts = useAppSelector(getReplaceTableFacts);
    const rowErrorList: Array<ErrorGrid> = useAppSelector(getSupplementalGridErrorList);
    const loggedUser: logerUser | null = useAppSelector(selectUserLogged);
    const dmap: any = useAppSelector(getDimensionSectionMap);


    const classStatus = {
        checked: "bi bi-check-circle text-success",
        error: "bi bi-exclamation-triangle-fill text-danger",
        lock: "bi bi-lock-fill text-warning",
        warning: "bi bi-exclamation-circle-fill text-warning",
        information: "bi bi-info-circle-fill text-warning",
    }


    /* Open the error list first and then expand the specific section to show the errors under it */
    const handleImageClick = (section: any) => (e: any) => {
        // Saving pending changes to AfrSlice
        if (selectedEntity && currentAfr && (pendingTableFacts || pendingReplaceTableFacts)) {
            utilityHelper.saveDeferredChanges(dispatch);
            const vc: ValidationContext = {
                afr: currentAfr,
                entity: selectedEntity,
                afrEvents: currentEvents,
                pendingTableFacts: [],
                pendingReplaceTableFacts: null,
                dimensionSectionMap: dmap
            };
            /* check if there are error to notify */
            var errors = getValidationsForAfr(vc);
            if (errors && errors.length > 0) {
                dispatch(validateAfr(vc));
            }
        }

        var globalErrors: Array<ErrorSummaryAFRSection> = JSON.parse(JSON.stringify(errorList));
        var indexCount = 0;

        for (var index in globalErrors) {
            globalErrors[index].section.sectionIsOpen = false;
            if (globalErrors[index].section.sectionCode === section) {
                globalErrors[index].section.sectionIsOpen = true;
            }
            indexCount = indexCount + 1;
        }

        if (indexCount > 0) {
            dispatch(updateErrors(globalErrors));
        }
        dispatch(setDisplayErrors(true));
    }
    const hasRowErrors = function (section: String) {
        if (rowErrorList && rowErrorList.length > 0 ) {
            var isSD = currentAfr ? currentAfr.afrId > "3" : false;
            switch( section ) {
                case enums.UISection.otherAfrData:
                    return isSD === true;

                case enums.UISection.affiliatedEntities:
                    return isSD === false;
            }
        }
        return false;
    }
    const evaluateStatus = function (section: string) {
        if (!currentAfr || !selectedEntity) {
            return [<React.Fragment></React.Fragment>];
        }
        var errorMessage: Array<AFRError> = [];
        var errors: boolean = hasRowErrors(section);
        if (errors === false) {
            errors = (getTotalErrorCountBySection(errorList, section) > 0);
        }
        var ok: boolean = (utilityHelper.evaluateSectionOK(loggedUser, currentAfr, selectedEntity, section, auditMessage, errorMessage, dispatch).length > 0);
        var lock: boolean = (evaluateLOCK(section).length > 0);
        
        utilityHelper.notifyErrors(currentAfr, section, errorMessage, dispatch);

        var render = [<React.Fragment></React.Fragment>];

        if (currentAfr.status !== enums.AFRStatus.not_reported) {

            if (lock) {
                render.push(<i className={classStatus.lock}></i>);
            }

            if (errors) {
                render.push(paintMaxErrorLevel(section));
                return render;
            }

            if (ok) {
                if (currentAfr.taxonomyType === "COURT_REPORTING_29") {
                    var factsValueFound = utilityHelper.getCR29FactsAndDocuments(currentAfr);
                    if (factsValueFound === "NoCPALetter") {
                        render.push(<i></i>);
                    } else if (factsValueFound === "HasFacts") {
                        render.push(<i className={classStatus.checked}></i>);
                    } else {
                        render.push(<i></i>);
                    }
                }
                else {
                    render.push(<i className={classStatus.checked}></i>);
                }
            }
        }

        return render;

    }



    const paintMaxErrorLevel = function (section: string) {
        if (!(errorList) || (errorList.length === 0)) {
            if (hasRowErrors(section)) {
                return <i className={classStatus.error} onClick={handleImageClick(section)} style={{ cursor: 'pointer' }} title="Click to see the errors" ></i>;
            }
            return <div></div>
        };
        var errors = errorList.find((x: any) => x.section.sectionCode === section);

        if ((typeof errors === "undefined")) {
            return <div></div>;
        }

        var index = errors.errorList.findIndex((x: any) => x.severity === enums.severity.error);
        if (index > -1) { return <i className={classStatus.error} onClick={handleImageClick(section)} style={{ cursor: 'pointer' }} title="Click to see the errors" ></i> }

        index = errors.errorList.findIndex((x: any) => x.severity === enums.severity.warning);
        if (index > -1) { return <i className={classStatus.warning} onClick={handleImageClick(section)} style={{ cursor: 'pointer' }} title="Click to see the warnings." ></i> }

        index = errors.errorList.findIndex((x: any) => x.severity === enums.severity.information);
        if (index > -1) { return <i className={classStatus.information} onClick={handleImageClick(section)} style={{ cursor: 'pointer' }} title="Click to see the information message."></i> }

        return <div></div>
    }

    const evaluateLOCK = function (section : string):string {
        switch (section) {
            case enums.UISection.summary:
            case enums.UISection.reports:
            case enums.UISection.errors:
            case enums.UISection.instructions:
                return "";
            default:
                if (utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)) {
                    return "LOCKED"
                }
                return "";
        }
    }



    return (
        <React.Fragment>
            {evaluateStatus(props.section)}
        </React.Fragment>
    )
}

export default NavigatorIconNotification;
